import {useContext, useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {reaction} from "mobx";

import classNames from "classnames";
import styles from "./styles/_BlockCS.module.scss";

import dynamic from 'next/dynamic';
import ErrorBlock from "../SchoolBlocks/ErrorBlock";
import {StoreContext} from "../../../stores/StoreLoader";
import WatsonApi from "../../../backends/WatsonApi";

export const updateContentItem = async (newContentItem: Partial<ContentItem>, newOrganization: BaseOrganization) => {
    const client = await WatsonApi();
    const response = await client.apis.organizations.organizations_content_items_partial_update({
        organization_pk: newContentItem.organization_id,
        id: newContentItem.id,
        data: {
            ...newContentItem,
            organization_id: newOrganization.id,
        },
    });
    return JSON.parse(response.data);
}
export const deleteContentItem = async (contentItem) => {
    const client = await WatsonApi();
    await client.apis.organizations.organizations_content_items_delete({
        id: contentItem.id,
        organization_pk: contentItem.organization_id,
    });
}

export const enum ITerraceTypes {
    EVENTS = "events",
    NEWS = "news",
    MEDIA = "media",
    ABOUT = "about",
    MAP = "map",
    HTML = "html",
    FILES = "files",
    HERO = "hero",
    LINKS = "links",
    STAFF = "staff",
    STATEMENT = "statement",
}

const blockTypes = {
    [ITerraceTypes.MAP]: dynamic(() => import("./MapBlock/MapBlock")),
    [ITerraceTypes.EVENTS]: dynamic(() => import("./EventsBlock/EventsBlock")),
    [ITerraceTypes.ABOUT]: dynamic(() => import("./AboutBlock/AboutBlock")),
    [ITerraceTypes.HTML]: dynamic(() => import("./HTMLBlock/HTMLBlock")),
    [ITerraceTypes.NEWS]: dynamic(() => import("./NewsBlock/NewsBlock")),
    [ITerraceTypes.MEDIA]: dynamic(() => import("./GalleryBlock/GalleryBlock")),
    [ITerraceTypes.FILES]: dynamic(() => import("./FilesFoldersBlock/FilesFoldersBlock")),
    [ITerraceTypes.LINKS]: dynamic(() => import("./LinksTerrace/LinksTerrace")),
    [ITerraceTypes.STAFF]: dynamic(() => import("./StaffBlock/StaffBlock")),
    [ITerraceTypes.STATEMENT]: dynamic(() => import("./StatementBlock/StatementBlock")),
};

export type ITerraceProps = {
    aboveTheFold: boolean,
    handleUpdateContentItem: (contentItem: ContentItem, organization: BaseOrganization) => void,
    handleDeleteContentItem: (contentItem: ContentItem) => void,
}

const Block = observer((props: {
    blockObj: ITerrace,
    aboveTheFold: boolean,
    firstTerraceStyle: string | null,
}) => {
    const {interfaceStore, gridStore, organizationStore} = useContext(StoreContext);

    const [aboveTheFold, setAboveTheFold] = useState(props.aboveTheFold);
    const blockRef = useRef<HTMLElement | null>(null);

    const BlockTypeComponent = blockTypes[props.blockObj.type] || ErrorBlock;

    useEffect(() => {
        const r = reaction(() => interfaceStore.scrollPosition, () => {
            if (blockRef?.current && !aboveTheFold) {
                const rect = blockRef.current?.getBoundingClientRect();
                if (rect && rect.top < (window.innerHeight - (window.innerHeight/4)) && !aboveTheFold) {
                    setAboveTheFold(true);
                }
            }
        }, {fireImmediately: true})
        return () => r();
    }, []);

    async function handleSaveContentItem(newContentItem, newOrganization) {
        const item = await updateContentItem(newContentItem, newOrganization);
        gridStore.setBlocks([{
            ...props.blockObj,
            content_items: props.blockObj.content_items?.map(c => {
                // expanded organization is not returned from an update, and it might have changed
                return c.id === item.id ? {...item, organization: newOrganization} : c;
            })
        }])
    }

    async function handleDeleteContentItem(contentItem) {
        await deleteContentItem(contentItem);
        gridStore.setBlocks([{
            ...props.blockObj,
            content_items: props.blockObj.content_items?.filter(c => c.id !== contentItem.id),
        }]);
    }

    const blockClassName = classNames({
        [styles.block]: true,
        [styles.blockAboveTheFold]: aboveTheFold,
        [styles.blockMapTerrace]: props.blockObj.type === ITerraceTypes.MAP,
    });

    return (
        <article
            className={`${blockClassName} ${props.firstTerraceStyle}`}
            data-id={props.blockObj.id}
            data-block-type={props.blockObj.type}
            ref={blockRef}
        >
            <BlockTypeComponent
                blockObj={props.blockObj}
                aboveTheFold={aboveTheFold}
                handleDeleteContentItem={handleDeleteContentItem}
                handleUpdateContentItem={handleSaveContentItem}
            />
        </article>
    );
});

export default Block;
